import { Box } from '@mui/material';

import type { CropArea } from '@modules/_shared/components/image-upload/types';

type NaturalSize = { naturalWidth: number; naturalHeight: number };

const rotatedBox = (
  { naturalWidth, naturalHeight }: NaturalSize,
  rotation: number,
) => {
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: naturalWidth * cos + naturalHeight * sin,
    height: naturalWidth * sin + naturalHeight * cos,
  };
};

export const CropPreview = ({
  imageUrl,
  mediaSize,
  cropArea,
  rotation,
  width,
  aspectRatio,
  round,
  flush = false,
}: {
  imageUrl: string;
  mediaSize: NaturalSize | null;
  cropArea: CropArea | null;
  rotation: number;
  width: number;
  aspectRatio: number;
  round: boolean;
  flush?: boolean;
}) => {
  const frameSx = {
    position: 'relative',
    width,
    aspectRatio: String(aspectRatio),
    flexShrink: 0,
    borderRadius: flush ? 0 : round ? '50%' : '8px',
    overflow: 'hidden',
    bgcolor: 'grey.100',
    border: flush ? 0 : 1,
    borderColor: 'divider',
  } as const;

  if (mediaSize == null || cropArea == null || cropArea.width <= 0) {
    return (
      <Box sx={frameSx}>
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
    );
  }

  const scale = width / cropArea.width;
  const box = rotatedBox(mediaSize, rotation);

  return (
    <Box sx={frameSx}>
      <Box
        sx={{
          position: 'absolute',
          left: -cropArea.x * scale,
          top: -cropArea.y * scale,
          width: box.width * scale,
          height: box.height * scale,
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt=""
          sx={{
            position: 'absolute',
            left: ((box.width - mediaSize.naturalWidth) * scale) / 2,
            top: ((box.height - mediaSize.naturalHeight) * scale) / 2,
            width: mediaSize.naturalWidth * scale,
            height: mediaSize.naturalHeight * scale,
            maxWidth: 'none',
            transform: `rotate(${rotation}deg)`,
          }}
        />
      </Box>
    </Box>
  );
};
